import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { PRODUCT_IMAGE, heroGuarantees, packDescription } from '../data/content';
const EASE = [0.23, 1, 0.32, 1] as const;
export function Hero() {
  return <section className="relative w-full overflow-hidden bg-seyes" data-id="element-107" data-mp="4lyw0o28qiep">
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-4 w-px bg-margin sm:left-8 lg:left-12" data-id="element-108" data-mp="4njng4t9dl8c" />

      <div className="relative mx-auto max-w-6xl px-8 pb-20 pt-6 sm:px-14 lg:pb-28" data-id="element-109" data-mp="x9un9d91ji43">
        <div className="flex items-center justify-between border-b border-ink/10 pb-5" data-id="element-110" data-mp="sb1bpqdbe16k">
          <span className="font-display text-xl tracking-tight text-ink sm:text-2xl" data-id="element-111" data-mp="nc87kojx3mj2">
            SCIENCES PRÊTES<span className="align-super text-[0.55em]" data-id="element-112" data-mp="3d1ihv20l0yjq">™</span>
          </span>
          <span className="text-sm font-medium text-ink/70" data-id="element-113" data-mp="3cz5to748dry8">Du CP au CM2</span>
        </div>

        <div className="mt-12 grid gap-14 lg:mt-16 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-12" data-id="element-114" data-mp="384jud2bfbpui">
          <div data-id="element-115" data-mp="3j6odfogqqus7">
            <motion.h1 initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: EASE
          }} className="font-display text-[3.1rem] leading-[0.95] tracking-tight text-ink sm:text-7xl lg:text-[5.4rem]" data-id="element-116" data-mp="3o7c1q2dl4xiq">
              Préparez une année entière de sciences en seulement{' '}
              <span className="relative inline-block whitespace-nowrap italic text-pen" data-id="element-117" data-mp="30g6lhm3qg37w">
                5 minutes
                <svg aria-hidden="true" viewBox="0 0 200 14" preserveAspectRatio="none" className="absolute -bottom-1.5 left-0 h-3 w-full sm:-bottom-2.5" data-id="element-118" data-mp="2qjv4iit76p74">
                  <motion.path d="M3 9 C 45 3, 95 2, 135 6 S 185 11, 197 4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" vectorEffect="non-scaling-stroke" initial={{
                  pathLength: 0
                }} animate={{
                  pathLength: 1
                }} transition={{
                  duration: 0.3,
                  delay: 0.35,
                  ease: EASE
                }} data-id="element-119" data-mp="2hkggrr4f0sqb" />
                </svg>
              </span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: 0.08,
            ease: EASE
          }} className="mt-8 max-w-xl text-lg leading-relaxed text-ink/75 sm:text-xl" data-id="element-120" data-mp="1b0nhtm9m9x1s">
              Arrêtez d’improviser. Entrez en classe avec vos supports prêts pour expliquer,
              expérimenter et inspirer.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: 0.16,
            ease: EASE
          }} className="mt-10" data-id="element-121" data-mp="3ltqclcptivti">
              <CtaButton href="#offer" data-id="element-122" data-mp="1x7871tlc81wr" />
              <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink/70" data-id="element-123" data-mp="21a4rzqycoa3v">
                {heroGuarantees.map(item => <li key={item} className="flex items-center gap-2" data-id="element-124" data-mp="268of3uoojrxt">
                    <CheckIcon className="h-4 w-4 text-pen" aria-hidden="true" data-id="element-125" data-mp="2b1ram8d0pbgn" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>

          <motion.figure initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3,
          delay: 0.12,
          ease: EASE
        }} className="relative mx-auto w-full max-w-md pt-10" data-id="element-126" data-mp="2kxu8h20u6nxs">
            <div aria-hidden="true" className="absolute right-0 top-0 z-10 hidden rotate-[-5deg] items-end gap-1 sm:flex" data-id="element-127" data-mp="umpqo3f6hozj">
              <svg viewBox="0 0 60 50" className="h-10 w-12 text-pen" fill="none" data-id="element-128" data-mp="znn7abkjiayx">
                <path d="M56 6 C 40 4, 18 12, 10 40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-129" data-mp="49t3ftjmawsc" />
                <path d="M4 32 L10 42 L18 34" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-130" data-mp="e8setjvrp6j1" />
              </svg>
              <span className="font-hand text-[1.7rem] leading-[0.9] text-pen" data-id="element-131" data-mp="3crpgzcdir1nm">
                6 volumes
                <br data-id="element-132" data-mp="3rgbvjbvbnpds" />+ 5 bonus
              </span>
            </div>
            <img src={PRODUCT_IMAGE} alt="Coffret SCIENCES PRÊTES : six volumes du CP au CM2 dont le laboratoire, avec guides, fiches et corrigés" className="relative mx-auto w-[85%] drop-shadow-[0_32px_36px_rgba(20,32,61,0.22)] sm:w-full" data-id="element-133" data-mp="3mpdhkixt33dp" />
            <figcaption className="mx-auto mt-8 max-w-sm text-center text-sm leading-relaxed text-ink/70" data-id="element-134" data-mp="19inje3t6iv0o">
              {packDescription}
            </figcaption>
          </motion.figure>
        </div>
      </div>
    </section>;
}